import { graphql } from 'react-relay';
import get from 'lodash.get';
import tracking from 'dibs-tracking';
import md5 from 'md5';
import { getEverLoggedIn, getGuestId } from 'dibs-cookie-jar';
import { localStorage, sessionStorage } from 'dibs-browser-storage';

const USER_EMAIL_TOKEN = 'userEmailToken';
const USER_EMAIL = 'userEmail';
const STORAGE_TRANSACTIONS_STATUS = 'userTransactionsStatus';
const BUYER_STATUS_NONE = 'NONE';
const BUYER_STATUS_CONFIRMED_MULTIPLE = 'CONFIRMED_MULTIPLE';
const BUYER_STATUS_CONFIRMED_ONE = 'CONFIRMED_ONE';
const BUYER_STATUS_SUBMITTED = 'SUBMITTED';
const BUYER_STATUS_INITIATED = 'INITIATED';
const BUYER_STATUSES_MAP = {
    [BUYER_STATUS_CONFIRMED_MULTIPLE]: 'confirmed|multiple orders',
    [BUYER_STATUS_CONFIRMED_ONE]: 'confirmed|one order',
    [BUYER_STATUS_SUBMITTED]: 'submitted|none confirmed',
    [BUYER_STATUS_INITIATED]: 'initiated|none submitted',
    [BUYER_STATUS_NONE]: 'no orders initiated',
};

/*
 * depending on transaction history, determine which value to add for the BUYER_STATUSES_MAP
*/
function getBuyerStatusFromTransactions(transactions) {
    const { initiatedTransactions, submittedTransactions, confirmedTransactions } = transactions;
    let buyerStatus;

    if (confirmedTransactions && confirmedTransactions.totalResults > 1) {
        buyerStatus = BUYER_STATUS_CONFIRMED_MULTIPLE;
    } else if (confirmedTransactions && confirmedTransactions.totalResults) {
        buyerStatus = BUYER_STATUS_CONFIRMED_ONE;
    } else if (submittedTransactions && submittedTransactions.totalResults) {
        buyerStatus = BUYER_STATUS_SUBMITTED;
    } else if (initiatedTransactions && initiatedTransactions.totalResults) {
        buyerStatus = BUYER_STATUS_INITIATED;
    } else {
        buyerStatus = BUYER_STATUS_NONE;
    }

    return { buyerStatus };
}

/*
 * fetches all transactions created by user using different filters
 * in order to track order history
*/
function getTransactionsStatus(transactions) {
    let totalTransactions = 0;
    let transactionsStatus = {
        buyerStatus: BUYER_STATUS_NONE,
        totalTransactions,
    };
    // if user has any transactions, go through them to determine which value from BUYER_STATUSES_MAP it should have
    if (transactions.allTransactions) {
        totalTransactions = transactions.allTransactions.totalResults;
        transactionsStatus = getBuyerStatusFromTransactions(transactions);
        Object.assign(transactionsStatus, {
            totalTransactions,
        });

        sessionStorage.setItem(STORAGE_TRANSACTIONS_STATUS, transactionsStatus);
    } else {
        // otherwise default to sessionStorage (dont think we currently use it in mobile) or no transactions at all
        transactionsStatus =
            sessionStorage.getItem(STORAGE_TRANSACTIONS_STATUS) || transactionsStatus;
    }
    if (transactionsStatus) {
        transactionsStatus.buyerStatus = BUYER_STATUSES_MAP[transactionsStatus.buyerStatus];
    }

    return transactionsStatus;
}

/*
 * create the userType tag according to user permissions.
 * VIP|Trade|Unknown|undefined (logged out)
*/
function getCustomerType(user) {
    if (user.serviceId) {
        const isTrade = user.isTrade ? 'Trade' : '';
        const hasVipPermission = user.hasVipPermission ? 'VIP' : '';
        return (
            `${isTrade}${isTrade && hasVipPermission ? ' and ' : ''}${hasVipPermission}` ||
            'consumer'
        );
    } else if (user.everLoggedIn) {
        return undefined;
    } else {
        return 'Unknown';
    }
}

/*
 * creates the user tracking data.
*/
function trackUserAttributes({ transactions = {}, user = {} }) {
    const userEmail = get(user, 'profile.email') || localStorage.getItem(USER_EMAIL);
    const transactionsStatus = getTransactionsStatus(transactions);

    /*
    * Appends cdn test value to data layer tracking object.
    * Here it only adds the value to global object variable,
    * tracking itself is done with 'tracking.addUserAttributes'
    */
    tracking.updateGtmPageInfo({
        cdnTest3: `newregpromo=${!!get(user, 'activePromotion')}`,
    });

    tracking.addUserAttributes({
        loginStatus: user.serviceId ? 'Logged In' : 'Logged Out',
        registrationStatus: user.everLoggedIn ? 'Registered' : 'Not Registered',
        userID: user.serviceId,
        guestID: getGuestId(document.cookie),
        userEmail: userEmail ? md5(userEmail) : undefined,
        userEmailAddress: user.serviceId ? userEmail : undefined,
        emailToken: localStorage.getItem(USER_EMAIL_TOKEN),
        userZip: get(user, 'profile.zipCode'),
        defaultCurrency: get(user, 'profile.currency'),
        firstSessionDate: user.firstSessionDate || undefined,
        contactDealerStatus: user.totalMessagesToDealers
            ? 'dealer contacted'
            : 'no dealer contacted',
        buyerStatus: transactionsStatus.buyerStatus,
        tradeStatus: user.verifiedTradeStatus,
        customerType: getCustomerType(user),
        tradeTier: get(user, 'tradeFirm.tier'),
        tradeFirmId: get(user, 'tradeFirm.serviceId'),
    });
}

export const userFragment = graphql`
    fragment userTracking_user on User {
        firstSessionDate
        isTrade: hasPermission(name: "B_TRADE_CHECKOUT")
        hasVipPermission: hasPermission(name: "B_VIP_CHECKOUT")
        profile {
            email
            zipCode
            currency
        }
        serviceId
        totalMessagesToDealers
        tradeFirm {
            serviceId
            tier
        }
        verifiedTradeStatus
        activePromotion {
            serviceId
        }
    }
`;

function getUserFragmentData(user) {
    const {
        firstSessionDate,
        isTrade,
        hasVipPermission,
        profile,
        serviceId,
        totalMessagesToDealers,
        tradeFirm,
        verifiedTradeStatus,
        activePromotion,
    } = user;
    return {
        firstSessionDate,
        isTrade,
        hasVipPermission,
        profile,
        serviceId,
        totalMessagesToDealers,
        tradeFirm,
        verifiedTradeStatus,
        activePromotion,
    };
}

export const viewerFragment = graphql`
    fragment userTracking_viewer on Viewer {
        allTransactions: transactions(buyerId: $userIds, first: 0) @include(if: $hasUserId) {
            totalResults
        }
        initiatedTransactions: transactions(
            buyerId: $userIds
            viewName: "buyerTracking.initiated"
            first: 0
        ) @include(if: $hasUserId) {
            totalResults
        }
        submittedTransactions: transactions(
            buyerId: $userIds
            viewName: "buyerTracking.submitted"
            first: 0
        ) @include(if: $hasUserId) {
            totalResults
        }
        confirmedTransactionsMostRecent: transactions(
            buyerId: $userIds
            viewName: "buyerTracking.confirmed"
            first: 0
        ) @include(if: $hasUserId) {
            totalResults
        }
    }
`;

function getViewerFragmentData(viewer) {
    const {
        allTransactions,
        initiatedTransactions,
        submittedTransactions,
        confirmedTransactionsMostRecent,
    } = viewer;
    return {
        allTransactions,
        initiatedTransactions,
        submittedTransactions,
        confirmedTransactions: confirmedTransactionsMostRecent,
    };
}

/*
 * should be called on initial page load for all entries.
 * determines user tracking data to send to dibs-tracking.
 */
export function init({ viewer = {}, user }) {
    const transactions = getViewerFragmentData(viewer);
    const everLoggedIn = getEverLoggedIn(document.cookie);
    const userFragmentData = user ? getUserFragmentData(user) : undefined;

    trackUserAttributes({ user: { ...userFragmentData, everLoggedIn }, transactions });
}



// WEBPACK FOOTER //
// ./src/utils/userTracking.js